import React, { useState, useMemo, useEffect } from 'react';
import { Edit, Trash2, X, Plus, ArrowLeft, ArrowRight } from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import Table from "../../base-component/Table/Table";
import Pagination from "../../base-component/Pagination/Pagination";
import Dialog from "../../base-component/Dialog/Dialog";
import toast from 'react-hot-toast';
import { addLanguageApi } from '../../Services/ApiServices';
import { getLanguageApi } from '../../Services/ApiServices';
import { updateLanguageApi } from '../../Services/ApiServices';
import { deleteLanguageApi } from '../../Services/ApiServices';


const Language = () => {
  const itemsPerPage = 10;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [editedLanguageName, setEditedLanguageName] = useState("");
  const [newLanguageName, setNewLanguageName] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [languages, setLanguages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
    

  const handleEdit = (language) => {
    setSelectedLanguage(language);
    setEditedLanguageName(language.language || language.name);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (language) => {
    setSelectedLanguage(language);
    setIsDeleteDialogOpen(true);
  };

  const handleEditConfirm = (values) => {
    if (selectedLanguage && values.name.trim()) {
      const languageId = selectedLanguage.language_id || selectedLanguage.id;
      
      if (!languageId) {
        toast.error("Language ID is missing");
        console.error("Selected language object:", selectedLanguage);
        return;
      }

      // Ensure language_id is a number if the API expects it
      let params = {
        language_id: Number(languageId) || languageId,
        language: values.name.trim()
      };
      
      console.log("Update API params:", params);
      console.log("Selected language full object:", selectedLanguage);
      
      updateLanguageApi(params)
        .then(res => {
          console.log("Update success:", res.data);
          toast.success(res.data.message || "Language updated successfully");
          fetchLanguages();
          setIsEditDialogOpen(false);
          setSelectedLanguage(null);
          setEditedLanguageName("");
        })
        .catch(err => {
          console.log("Update error response:", err?.response);
          console.log("Update error data:", err?.response?.data);
          toast.error(err?.response?.data?.message || err?.response?.data?.error?.message || "Failed to update language");
        });
    } else {
      setIsEditDialogOpen(false);
      setSelectedLanguage(null);
      setEditedLanguageName("");
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedLanguage) {
      const languageId = selectedLanguage.language_id || selectedLanguage.id;
      
      if (!languageId) {
        toast.error("Language ID is missing");
        console.error("Selected language object:", selectedLanguage);
        return;
      }

      // Ensure language_id is a number if the API expects it
      let params = {
        language_id: Number(languageId) || languageId
      };
      
      console.log("Delete API params:", params);
      console.log("Selected language full object:", selectedLanguage);
      
      deleteLanguageApi(params)
        .then(res => {
          console.log("Delete success:", res.data);
          toast.success(res.data.message || "Language deleted successfully");
          fetchLanguages();
          setIsDeleteDialogOpen(false);
          setSelectedLanguage(null);
        })
        .catch(err => {
          console.log("Delete error response:", err?.response);
          console.log("Delete error data:", err?.response?.data);
          toast.error(err?.response?.data?.message || err?.response?.data?.error?.message || "Failed to delete language");
        });
    } else {
      setIsDeleteDialogOpen(false);
      setSelectedLanguage(null);
    }
  };

  const handleAddLanguage = () => {
    setNewLanguageName("");
    setIsAddDialogOpen(true);
  };

  const handleAddConfirm = (values) => {
    if (values.name.trim()) {
      let params = {
        language: values.name.trim()
      }
      addLanguageApi(params)
      .then(res => {
        console.log(res.data);
        toast.success(res.data.message);
        // Refresh the list after adding
        fetchLanguages();
      })
      .catch(err => {
        console.log(err);
        toast.error(err.response.data.message);
      })
    }
    setIsAddDialogOpen(false);
    setNewLanguageName("");
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must not exceed 100 characters")
      .trim(),
  });

  const startIndex = (pageNo - 1) * itemsPerPage;
  const currentLanguages = languages;

  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const renderPaginationButtons = () => {
    const maxPagesToShow = 3;
    const buttons = [];
    const startPage = Math.max(1, pageNo - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(pageCount, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={
            i === pageNo
              ? "bg-[#2C5BA7] text-white w-10 h-10 rounded-lg font-medium text-xl"
              : "text-[#4B4B4B] w-10 h-10 rounded-lg font-medium text-xl"
          }
        >
          {i}
        </button>
      );
    }
    return buttons;
  };
 

  const fetchLanguages = () => {
    let params = { 
      page: pageNo 
    };
    getLanguageApi(params)
      .then(res => {
        console.log("Language API Response:", res.data); // Debug log
        const apiLanguages = res?.data?.data || [];
        const total = res?.data?.total || res?.data?.total_count || res?.data?.count || 0;
        const totalPagesCount = res?.data?.total_pages || res?.data?.totalPages || res?.data?.last_page || (total > 0 ? Math.ceil(total / itemsPerPage) : 1);
        
        setLanguages(apiLanguages);
        setTotalCount(total);
        setPageCount(totalPagesCount > 0 ? totalPagesCount : 1);
      })
      .catch(err => {
        console.log(err);
        toast.error(err?.response?.data?.message || "Something went wrong");
        setLanguages([]);
        setPageCount(1);
        setTotalCount(0);
      });
  }
  useEffect(() => {
    fetchLanguages();
  }, [pageNo]);
  


  return (
    <>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <p className="text-xl p-0 m-0 font-bold text-gray-900 tracking-tight">Language</p>

        <button
          onClick={handleAddLanguage}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-[#2C5BA7] text-white rounded-lg hover:bg-[#1e4a8a] transition-colors font-medium"
        >
          <Plus size={18} />
          Add Language
        </button>
      </div>

      {/* Table Wrapper */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <Table>
          <Table.Thead variant="light">
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>LANGUAGE NAME</Table.Th>
              <Table.Th>CREATED AT</Table.Th>
              <Table.Th className="text-center">ACTIONS</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {currentLanguages.map((language, index) => (
              <Table.Tr key={language.language_id}>
                {/* ID */}
                <Table.Td className="font-semibold text-slate-700">
                  {startIndex + index + 1}
                </Table.Td> 

                {/* Language Name */}
                <Table.Td className="text-slate-600 font-medium">
                  {language.language}
                </Table.Td>

                {/* Created At */}
                <Table.Td className="text-slate-600">{moment(language.created_at).format("DD-MM-YYYY h:mm A") || "—"}</Table.Td>


                {/* Actions */}
                <Table.Td>
                  <div className="flex items-center justify-center gap-4">
                    {/* Edit */}
                    <button
                      onClick={() => handleEdit(language)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: '#10B981',
                        backgroundColor: '#D1FAE5',
                        color: '#10B981'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#059669';
                        e.target.style.backgroundColor = '#A7F3D0';
                        e.target.style.color = '#059669';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#10B981';
                        e.target.style.backgroundColor = '#D1FAE5';
                        e.target.style.color = '#10B981';
                      }}
                    >
                      <Edit size={16} />
                    </button>

                    {/* Delete */}
                    <button 
                      onClick={() => handleDelete(language)}
                      className="p-2 rounded-lg transition border"
                      style={{ 
                        borderColor: '#F87171',
                        backgroundColor: '#FEF2F2',
                        color: '#F87171'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = '#ef4444';
                        e.target.style.backgroundColor = '#FEE2E2';
                        e.target.style.color = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = '#F87171';
                        e.target.style.backgroundColor = '#FEF2F2';
                        e.target.style.color = '#F87171';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>

      {/* Pagination */}
      {languages.length > 0 && (
        <div className="flex mt-5 items-center col-span-12 justify-between">
          <Pagination>
            <Pagination.Link>
              <button
                className='rounded-lg p-1.5 hover:bg-[#101828] hover:text-white transition-all duration-300 cursor-pointer font-bold text-base box border border-[#EDEDED] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handlePageChange.bind(null, pageNo - 1)}
                disabled={pageNo == 1}
              >
                <ArrowLeft />
              </button>
            </Pagination.Link>
            <div className="flex items-center">
              {renderPaginationButtons()}
            </div>
            <Pagination.Link>
              <button
                className='rounded-lg p-1.5 hover:bg-[#101828] hover:text-white transition-all duration-300 cursor-pointer font-bold text-base box border border-[#EDEDED] disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handlePageChange.bind(null, pageNo + 1)}
                disabled={pageNo === pageCount}
              >
                <ArrowRight />
              </button>
            </Pagination.Link>
          </Pagination>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setSelectedLanguage(null);
          setEditedLanguageName("");
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="bg-white px-6 py-8 w-full relative">
              <button
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedLanguage(null);
                  setEditedLanguageName("");
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-6">
                Edit Language
              </div>

              <Formik
                initialValues={{ name: editedLanguageName }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={(values) => handleEditConfirm(values)}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                        Language Name
                      </label>
                      <Field
                        type="text"
                        name="name"
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5BA7] focus:border-transparent ${
                          errors.name && touched.name
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter language name"
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 h-12 shadow cursor-pointer bg-[#2C5BA7] text-white font-medium rounded-xl hover:bg-[#1e4a8a] transition-colors"
                    >
                      Update
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>

      {/* Add Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          setNewLanguageName("");
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="bg-white px-6 py-8 w-full relative">
              <button
                onClick={() => {
                  setIsAddDialogOpen(false);
                  setNewLanguageName("");
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-6">
                Add Language
              </div>

              <Formik
                initialValues={{ name: newLanguageName }}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  handleAddConfirm(values);
                  setNewLanguageName("");
                }}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                        Language Name
                      </label>
                      <Field
                        type="text"
                        name="name"
                        className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2C5BA7] focus:border-transparent ${
                          errors.name && touched.name
                            ? "border-red-500"
                            : "border-gray-300"
                        }`}
                        placeholder="Enter language name"
                      />
                      <ErrorMessage
                        name="name"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full px-6 h-12 shadow cursor-pointer bg-[#2C5BA7] text-white font-medium rounded-xl hover:bg-[#1e4a8a] transition-colors"
                    >
                      Add
                    </button>
                  </Form>
                )}
              </Formik>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedLanguage(null);
        }}
        className="relative z-50"
        size="md"
      >
        <Dialog.Panel>
          <Dialog.Description className="h-auto overflow-auto rounded-2xl p-0">
            <div className="px-4 py-4 text-center relative" style={{ backgroundColor: '#FEF2F2' }}>
              <button
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setSelectedLanguage(null);
                }}
                className="absolute cursor-pointer top-4 right-4 w-6 h-6 bg-[#475467] rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X size={18} />
              </button>

              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full flex items-center justify-center">
                  <Trash2 size={56} className="text-[#F87171]" />
                </div>
              </div>
            </div>

            <div className="bg-white px-6 py-8 text-center w-full">
              <div className="md:text-2xl text-base w-full m-auto text-center font-semibold text-gray-800 mb-4">
                Delete Language?
              </div>

              <Dialog.Description className="md:text-base text-sm text-gray-600 mb-6 leading-relaxed">
                Are you sure you want to delete <span className="font-semibold text-gray-900">{selectedLanguage?.language || selectedLanguage?.name}</span>? This action cannot be undone.
              </Dialog.Description>

              <button
                onClick={handleDeleteConfirm}
                className="w-full px-6 h-12 shadow cursor-pointer bg-[#F87171] text-white font-medium rounded-xl hover:bg-[#ef4444] transition-colors"
              >
                Yes, Delete
              </button>
            </div>
          </Dialog.Description>
        </Dialog.Panel>
      </Dialog>
    </>
  );
};

export default Language;
